import { useMemo } from 'react'

import { AttachmentFilterType } from '@features/attachments/enums/attachmentFiltertype'
import { useIsEInvoicingOnly } from '@features/bills/hooks/useIsEInvoicingOnly'

import { Scope } from '../../../enums/scope'
import { isAuthorized } from '../../../utils/isAuthorized'
import { useScoutAccess } from './useScoutAccess'

export interface UploadsTabType {
  type: AttachmentFilterType
  labelKey: string
}

const RECEIPTS_TAB_GATING_RULES: Record<AttachmentFilterType, (args: { isEInvoicingOnly: boolean }) => boolean> = {
  [AttachmentFilterType.All]: ({ isEInvoicingOnly }) => !isEInvoicingOnly,
  [AttachmentFilterType.Upload]: ({ isEInvoicingOnly }) => !isEInvoicingOnly,
  [AttachmentFilterType.Scout]: ({ isEInvoicingOnly }) => !isEInvoicingOnly,
  [AttachmentFilterType.EDocument]: () => true,
  [AttachmentFilterType.Blocked]: () => true,
  [AttachmentFilterType.Approved]: ({ isEInvoicingOnly }) => isEInvoicingOnly,
}

function tabGated({ tabs, isEInvoicingOnly }: { tabs: UploadsTabType[]; isEInvoicingOnly: boolean }) {
  return tabs.filter((tab) => {
    const tabFilteringFunction = RECEIPTS_TAB_GATING_RULES[tab.type]
    return tabFilteringFunction({ isEInvoicingOnly })
  })
}

export const useReceiptsTabs = ({ hasReceivedEInvoices }: { hasReceivedEInvoices: boolean }) => {
  const isEInvoicingEnabled = isAuthorized(Scope.EDocumentRead)
  const isEInvoicingOnly = useIsEInvoicingOnly()
  const { shouldShowScout } = useScoutAccess()

  const tabs: UploadsTabType[] = useMemo(() => {
    const result: UploadsTabType[] = [
      { type: AttachmentFilterType.All, labelKey: 'receipts.filters.tab.all' },
      { type: AttachmentFilterType.Upload, labelKey: 'receipts.filters.tab.manual' },
    ]

    if (shouldShowScout) {
      result.push({ type: AttachmentFilterType.Scout, labelKey: 'receipts.filters.tab.scout' })
    }

    if (isEInvoicingEnabled) {
      result.push({
        type: AttachmentFilterType.EDocument,
        labelKey: isEInvoicingOnly ? 'receipts.filters.tab.to_review' : 'receipts.filters.tab.e_invoices',
      })

      if (isEInvoicingOnly) {
        result.push({ type: AttachmentFilterType.Approved, labelKey: 'receipts.filters.tab.approved' })
      }

      if (hasReceivedEInvoices) {
        result.push({ type: AttachmentFilterType.Blocked, labelKey: 'receipts.filters.tab.blocked' })
      }
    }

    return tabGated({
      tabs: result,
      isEInvoicingOnly,
    })
  }, [hasReceivedEInvoices, isEInvoicingEnabled, isEInvoicingOnly, shouldShowScout])

  return tabs
}
